import { RRule, rrulestr } from "rrule";

/**
 * Converts milliseconds to a human-readable time format
 * Returns seconds if < 60s, minutes if < 60min, hours otherwise
 */
export function formatDelayTime(ms: number): string {
  const seconds = ms / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;

  if (seconds < 60) {
    return `${seconds}s`;
  } else if (minutes < 60) {
    return `${minutes.toFixed(minutes % 1 === 0 ? 0 : 1)}m`;
  } else {
    return `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h`;
  }
}

function getTimezoneAbbr(): string {
  return (
    new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(new Date())
      .find(p => p.type === "timeZoneName")?.value ?? "local"
  );
}

function formatTime12h(hour: number, minute: number): string {
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return minute === 0 ? `${h12} ${ampm}` : `${h12}:${minute.toString().padStart(2, "0")} ${ampm}`;
}

const WEEKDAY_FULL_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const REVERSE_FREQ_MAP: Record<number, string> = {
  [RRule.SECONDLY]: "second",
  [RRule.MINUTELY]: "minute",
  [RRule.HOURLY]: "hour",
  [RRule.DAILY]: "day",
  [RRule.WEEKLY]: "week",
  [RRule.MONTHLY]: "month",
};

/**
 * Formats an RRULE string to a human-readable string with local timezone.
 * Converts UTC weekday/month-day references back to local equivalents so the
 * description matches what the user actually selected.
 */
export function formatRRule(rruleString: string): string {
  // Unresolved template variables (e.g. `{{$props.cronjob_rrule}}`) arrive
  // when a workflow template is rendered before its props are instantiated.
  // `rrulestr()` throws "Unknown RRULE property" on these, which spams the
  // console with `Failed to parse RRULE` errors. Match the convention used
  // by `formatTokenAmount` below and pass the placeholder through untouched
  // so the preview UI can show it as-is (or upstream callers can substitute
  // their own copy via the `{{` check).
  if (rruleString?.includes("{{")) return rruleString;

  try {
    const rule = rrulestr(rruleString);
    const opts = rule.options;

    if (opts.freq === RRule.SECONDLY) {
      const interval = opts.interval || 1;
      return interval === 1 ? "Every second" : `Every ${interval} seconds`;
    }

    // Calculate UTC day shift so we can reverse it for display
    const dt = opts.dtstart;
    let utcDayShift = 0;
    if (dt) {
      utcDayShift = dt.getUTCDay() - dt.getDay();
      if (utcDayShift > 1) utcDayShift -= 7;
      if (utcDayShift < -1) utcDayShift += 7;
    }

    const singular = REVERSE_FREQ_MAP[opts.freq] ?? "interval";
    const interval = opts.interval || 1;
    let text = interval === 1 ? `Every ${singular}` : `Every ${interval} ${singular}s`;

    // Weekdays — reverse the UTC shift to show local weekday names
    if (opts.freq === RRule.WEEKLY && opts.byweekday && opts.byweekday.length > 0) {
      const localDays = opts.byweekday
        .map((wd: number) => (wd - utcDayShift + 7) % 7)
        .sort((a: number, b: number) => a - b)
        .map((i: number) => WEEKDAY_FULL_NAMES[i]);
      text += ` on ${localDays.join(", ")}`;
    }

    // Month days — reverse the UTC shift
    if (opts.freq === RRule.MONTHLY && opts.bymonthday && opts.bymonthday.length > 0) {
      const localDays = opts.bymonthday
        .map((d: number) => Math.max(1, Math.min(28, d - utcDayShift)))
        .sort((a: number, b: number) => a - b);
      const suffix = (n: number) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
      };
      text += ` on the ${localDays.map((d: number) => `${d}${suffix(d)}`).join(", ")}`;
    }

    // Append local time for daily/weekly/monthly
    if (dt && opts.freq <= RRule.DAILY) {
      text += ` at ${formatTime12h(dt.getHours(), dt.getMinutes())} ${getTimezoneAbbr()}`;
    }

    return text;
  } catch (error) {
    console.error("Failed to parse RRULE:", error);
    return rruleString;
  }
}

/**
 * Pretty format filter JSON with proper indentation
 */
export function formatFilter(filter: unknown): string {
  try {
    if (typeof filter === "string") {
      // Try to parse if it's a JSON string
      const parsed = JSON.parse(filter);
      return JSON.stringify(parsed, null, 2);
    }
    if (typeof filter === "object" && filter !== null) {
      return JSON.stringify(filter, null, 2);
    }
    return String(filter);
  } catch {
    // If not valid JSON, return as-is
    return String(filter);
  }
}

function formatHumanAmount(humanAmount: number): string {
  if (humanAmount === 0) return "0";
  if (humanAmount >= 1_000_000_000) {
    return (humanAmount / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "B";
  }
  if (humanAmount >= 1_000_000) {
    return (humanAmount / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "M";
  }
  if (humanAmount >= 1_000) {
    return (humanAmount / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "K";
  }
  if (humanAmount >= 1) {
    return humanAmount.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (humanAmount >= 0.000001) {
    return humanAmount.toLocaleString(undefined, { maximumFractionDigits: 6 }).replace(/0+$/, "").replace(/\.$/, "");
  }
  const exponent = Math.floor(Math.log10(humanAmount));
  const precision = Math.min(Math.abs(exponent) + 2, 20);
  return humanAmount.toFixed(precision).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Formats a token amount intelligently for display
 * - Converts from wei (raw units) to human-readable based on decimals
 * - Uses BigInt for precision with large integer strings
 * - Shows normal numbers with up to 6 significant decimals
 * - Shows large numbers with abbreviations (K, M, B)
 *
 * @param amount - The raw amount string (in wei/smallest unit)
 * @param decimals - The token's decimal places (default 18)
 * @returns Formatted string for display
 */
export function formatTokenAmount(amount: string | number | undefined, decimals: number = 18): string {
  if (amount === undefined || amount === null || amount === "") return "...";

  const amountStr = String(amount);

  // If it contains template variables, return as-is
  if (amountStr.includes("{{")) return amountStr;

  // Non-integer string (e.g., already formatted) — fall back to parseFloat
  if (!/^\d+$/.test(amountStr)) {
    const numericValue = parseFloat(amountStr);
    if (isNaN(numericValue)) return amountStr;
    if (numericValue === 0) return "0";
    return formatHumanAmount(numericValue / Math.pow(10, decimals));
  }

  if (amountStr === "0") return "0";

  // BigInt division for correct integer-part precision; fractional digits trimmed to display precision
  const raw = BigInt(amountStr);
  const divisor = BigInt(10) ** BigInt(decimals);
  const whole = raw / divisor;
  const remainder = raw % divisor;

  if (remainder === 0n) {
    return formatHumanAmount(Number(whole));
  }

  // Trim to 6 significant decimal digits — formatHumanAmount never shows more
  const fracStr = remainder.toString().padStart(decimals, "0").slice(0, 6);
  return formatHumanAmount(Number(`${whole}.${fracStr}`));
}
